import axios from "axios";
import gfm from "remark-gfm";
import ReactMarkdown from "react-markdown";
import React, { useState, useRef, useEffect } from "react";
import { debounce } from "lodash";
import { Button, Input, InputGroup, InputGroupAddon } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash, faStop, faRedo, faSearch, faTimes } from "@fortawesome/free-solid-svg-icons";
import SendIcon from "@material-ui/icons/Send";
import "./ChatBot.css";
import ChatBotIcon from "./ChatBotIcon.svg";
import useAudioRecorder from "./useAudioRecorder.js";
import { faMicrophone, faMicrophoneSlash } from "@fortawesome/free-solid-svg-icons";

const ChatBot = ({ toggleChatBot, isChatBotOpen }) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const [lastQuestion, setLastQuestion] = useState("");
  const [conversationId, setConversationId] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suppressSuggestions, setSuppressSuggestions] = useState(false);
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1);
  const [buttonFlip, setButtonFlip] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [pendingEmail, setPendingEmail] = useState("");
  const [interruptInputs, setInterruptInputs] = useState({});
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [emailConfirmed, setEmailConfirmed] = useState(false);
  const [emailError, setEmailError] = useState({})

  const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

  const { isRecording, startRecording, stopRecording } = useAudioRecorder((transcribedText) => {
    setInput(transcribedText);
  });

  const accumulatedTokensRef = useRef("");
  const pendingBotTextRef = useRef("");
  const actionDetectedRef = useRef(null);
  const messagesEndRef = useRef(null);
  const controllerRef = useRef(null);
  const messageIdCounter = useRef(0);
  const inputContainerRef = useRef(null);
  const inputRef = useRef(null);

  const CHATBOT_API_URL = "https://stream.gizmott.com/chat-agent"

  const toggle = () => toggleChatBot();

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        inputContainerRef.current &&
        !inputContainerRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (isTyping) {
      setShowSuggestions(false);
    }
  }, [isTyping]);

  useEffect(() => {
    setButtonFlip(true);
    const timer = setTimeout(() => {
      setButtonFlip(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [isTyping]);

  const getNewMessageId = () => {
    messageIdCounter.current += 1;
    return messageIdCounter.current;
  };

  const stopGenerating = () => {
    if (controllerRef.current) {
      controllerRef.current.abort();
      controllerRef.current = null;
      setIsTyping(false);
      setMessages((prevMessages) =>
        prevMessages.map((msg, index) =>
          index === prevMessages.length - 1 &&
            msg.sender === "bot" &&
            msg.isStreaming
            ? { ...msg, isStreaming: false, interrupted: true }
            : msg
        )
      );
    }
  };

  const retryLastQuestion = () => {
    if (lastQuestion) {
      const lastBotMessage = [...messages]
        .reverse()
        .find((msg) => msg.sender === "bot");
      lastBotMessage && sendMessage(lastQuestion, messages, lastBotMessage.id);
    }
  };

  const handleResume = async (interrupt_id, user_input, email) => {
    if (!interrupt_id || !user_input) {
      console.error("Missing interrupt_id or user_input");
      return;
    }
    try {
      setMessages((prev) =>
        prev.map((msg) =>
          msg.interrupt_id === interrupt_id
            ? { ...msg, buttonsDisabled: true }
            : msg
        )
      );

      const userMsg = { id: getNewMessageId(), text: user_input, sender: "user" };
      setMessages((prev) => [...prev, userMsg]);
      
      const res = await fetch(`${CHATBOT_API_URL}/resume`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "access-token": localStorage.getItem("access-token"),
          role_id: localStorage.getItem("role_id"),
          pubid: localStorage.getItem("pub_id"),
        },
        body: JSON.stringify({ interrupt_id, user_input, email }),
      });
      
      if (!res.ok) {
        throw new Error(`Resume request failed: ${res.status}`);
      }

      if (!res.body) {
        const json = await res.json().catch(() => null);
        if (json) {
          if (json.interrupt) {
            setMessages((prev) => [
              ...prev,
              {
                id: getNewMessageId(),
                text: json.message || json.content || "",
                sender: "bot",
                type: "interrupt",
                interrupt_id: json.interrupt_id,
                waiting_for_email: json.waiting_for_email || false,
                email: json.email || "",
              },
            ]);
          } else if (json.text) {
            setMessages((prev) => [
              ...prev,
              { id: getNewMessageId(), text: json.text, sender: "bot" },
            ]);
          }
        }
        return;
      }
      
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let accumulated = "";
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        accumulated += decoder.decode(value, { stream: true });
        
        while (accumulated.includes("\n\n")) {
          const idx = accumulated.indexOf("\n\n");
          const chunk = accumulated.slice(0, idx);
          accumulated = accumulated.slice(idx + 2);
          const m = chunk.match(/data:(.*)/s);
          if (!m) continue;
          let raw = m[1].trim();
          if (raw === "[DONE]") continue;
          let data = null;
          try {
            data = JSON.parse(raw);
          } catch (e) {
            console.warn("Invalid resume SSE JSON:", raw);
            continue;
          }
          
          if (data.interrupt || data.type === "interrupt") {
            const interruptMsg = {
              id: getNewMessageId(),
              text: data.message || data.content || "",
              sender: "bot",
              type: "interrupt",
              interrupt_id: data.interrupt_id || interrupt_id,
              waiting_for_email: data.waiting_for_email || false,
              email: data.email || email || "",
            };
            setMessages((prev) => [...prev, interruptMsg]);
            if (interruptMsg.waiting_for_email && data.email) {
              setInterruptInputs((prev) => ({
                ...prev,
                [interruptMsg.interrupt_id]: data.email,
              }));
            }
            reader.cancel();
            return;
          }
          
          if (data.type === "token") {
            console.log("Token received:", data.content);
            let textChunk = data.content;
            textChunk = textChunk.replace(/\\n/g, "\n");
            textChunk = textChunk.replace(/\\\n/g, "\n");
            setMessages((currentMessages) => {
              const lastMessage = currentMessages[currentMessages.length - 1];
              if (lastMessage?.sender === "bot" && lastMessage.isStreaming) {
                return currentMessages.map((msg, i) =>
                  i === currentMessages.length - 1
                    ? { ...msg, text: msg.text + textChunk }
                    : msg
                );
              }
              const newBotMessageId = getNewMessageId();
              return [
                ...currentMessages,
                {
                  id: newBotMessageId,
                  text: textChunk,
                  sender: "bot",
                  isStreaming: true,
                },
              ];
            });
            continue;
          }
          
          if (data.type === "done") {
            setMessages((currentMessages) => {
              const updatedMessages = [...currentMessages];
              const lastMessageIndex = updatedMessages.length - 1;
              if (lastMessageIndex >= 0 && updatedMessages[lastMessageIndex]?.sender === "bot" && updatedMessages[lastMessageIndex].isStreaming) {
                updatedMessages[lastMessageIndex] = {
                  ...updatedMessages[lastMessageIndex],
                  isStreaming: false,
                };
              }
              return updatedMessages;
            });
            continue;
          }
        }
      }
    } catch (err) {
      console.error("Error in handleResume:", err);
    }
  };

  const sendMessage = async (messageText, prevMessages, replaceId = null) => {
    setIsTyping(true);
    accumulatedTokensRef.current = "";
    pendingBotTextRef.current = "";
    actionDetectedRef.current = null;

    try {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      controllerRef.current = new AbortController();
      const signal = controllerRef.current.signal;

      const pubid = localStorage.getItem("pub_id");
      const currentConversationId =
        conversationId ||
        (() => {
          const newId = `${pubid}_${Math.floor(Math.random() * 100000) + 1}`;
          setConversationId(newId);
          return newId;
        })();

      const response = await fetch(`${CHATBOT_API_URL}/support`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "access-token": localStorage.getItem('access-token'),
          role_id: localStorage.getItem("role_id"),
          pubid: localStorage.getItem("pub_id")
        },
        body: JSON.stringify({
          message: messageText,
          pubid,
          conversation_id: currentConversationId,
        }),
        signal: signal,
      });

      if (!response.ok) {
        throw new Error("Network response was not ok");
      }

      const reader = response.body.getReader();
      let accumulatedText = "";
      const isReplacingMessage = replaceId !== null;
      let replacementText = "";
      let tokenCount = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = new TextDecoder().decode(value);
        accumulatedText += chunk;

        while (accumulatedText.includes("\n\n")) {
          const eventEnd = accumulatedText.indexOf("\n\n");
          const eventText = accumulatedText.slice(0, eventEnd);
          accumulatedText = accumulatedText.slice(eventEnd + 2);

          const dataMatch = eventText.match(/data: (.*)/);
          if (dataMatch) {
            const data = JSON.parse(dataMatch[1]);

            if (data.type === "token") {
              const tokenContent = data.content.trim().toLowerCase();
              accumulatedTokensRef.current += tokenContent;
              const normalized = accumulatedTokensRef.current.replace(/\s+/g, "");
              if (normalized.includes("password_reset") || (normalized.includes("password") && normalized.includes("reset"))) {
                actionDetectedRef.current = "password_reset";
                continue;
              }
              if (actionDetectedRef.current) {
                pendingBotTextRef.current += data.content;
                continue;
              }

              tokenCount++;
              if (tokenCount > 4096) {
                console.warn("Token limit exceeded");
                stopGenerating();
                break;
              }

              let processedContent = data.content;
              processedContent = processedContent.replace(/\\n/g, "\n");
              processedContent = processedContent.replace(/\\\n/g, "\n");

              if (isReplacingMessage) {
                replacementText += processedContent;
                setMessages((currentMessages) =>
                  currentMessages.map((msg) =>
                    msg.id === replaceId
                      ? { ...msg, text: replacementText, isStreaming: true }
                      : msg
                  )
                );
              } else {
                setMessages((currentMessages) => {
                  const lastMessage = currentMessages[currentMessages.length - 1];
                  if (lastMessage?.sender === "bot" && lastMessage.isStreaming) {
                    const newText = lastMessage.text + processedContent;
                    return currentMessages.map((msg, i) =>
                      i === currentMessages.length - 1
                        ? { ...msg, text: newText } 
                        : msg
                    );
                  }
                  return [
                    ...currentMessages,
                    {
                      id: getNewMessageId(),
                      text: processedContent,
                      sender: "bot",
                      isStreaming: true,
                    },
                  ];
                });
              }
            } else if (data.type === "interrupt") {
              const { interrupt_id, waiting_for_email, content } = data;
              const newMsgId = getNewMessageId();

              setMessages((prev) => {
                const updated = prev.filter(
                  (msg, i) => !(msg.sender === "bot" && msg.isStreaming && i === prev.length - 1)
                );

                return [
                  ...updated,
                  {
                    id: newMsgId,
                    text: content || data.message || "",
                    sender: "bot",
                    type: "interrupt",
                    interrupt_id,
                    waiting_for_email,
                  },
                ];
              });
            } else if (data.type === "done") {
              if (actionDetectedRef.current === "password_reset") {
                setMessages((currentMessages) => {
                  const filtered = currentMessages.filter(
                    (msg, i) => !(msg.sender === "bot" && msg.isStreaming && i === currentMessages.length - 1)
                  );
                  const existingEmailMessage = prevMessages.find(
                    (m) => m.sender === "user" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m.text)
                  );

                  if (existingEmailMessage) {
                    setPendingEmail(existingEmailMessage.text);
                    setPendingAction("confirm_email");

                    return [
                      ...filtered,
                      {
                        id: getNewMessageId(),
                        text: "",
                        sender: "bot",
                        isStreaming: false,
                        isDynamicUI: true,
                      },
                    ];
                  } else {
                    setPendingAction("ask_email");

                    return [
                      ...filtered,
                      {
                        id: getNewMessageId(),
                        text: "",
                        sender: "bot",
                        isStreaming: false,
                        isDynamicUI: true,
                      },
                    ];
                  }
                });
              } else {
                setMessages((currentMessages) => {
                  const updatedMessages = [...currentMessages];
                  const messageIndex = isReplacingMessage
                    ? updatedMessages.findIndex((msg) => msg.id === replaceId)
                    : updatedMessages.length - 1;

                  if (messageIndex !== -1 && updatedMessages[messageIndex]?.sender === "bot") {
                    let messageText = updatedMessages[messageIndex].text;
                    updatedMessages[messageIndex] = {
                      ...updatedMessages[messageIndex],
                      text: messageText,
                      isStreaming: false,
                    };
                  }
                  return updatedMessages;
                });
              }
              if (data.action) {
                console.log("Received action from backend:", data.action);

                if (data.action === "password_reset") {
                  const existingEmailMessage = prevMessages.find(
                    (m) => m.sender === "user" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(m.text)
                  );

                  if (existingEmailMessage) {
                    setPendingEmail(existingEmailMessage.text);
                    setEmailSubmitted(false);
                    setEmailConfirmed(false);
                    setPendingAction("confirm_email");
                  } else {
                    setEmailSubmitted(false);
                    setEmailConfirmed(false);
                    setPendingAction("ask_email");
                  }
                } else if (data.action === "ask_email") {
                  setEmailSubmitted(false);
                  setEmailConfirmed(false);
                  setPendingAction("ask_email");
                } else if (data.action === "confirm_email") {
                  setPendingEmail(data.email || "");
                  setEmailSubmitted(false);
                  setEmailConfirmed(false);
                  setPendingAction("confirm_email");
                }
              }
            }
          }
        }
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Error:", error);
        setMessages((msg) => [
          ...msg,
          { id: getNewMessageId(), text: "Error. Try again.", sender: "bot" },
        ]);
      }
    } finally {
      setIsTyping(false);
      controllerRef.current = null;
    }
  };

  const handleSend = async () => {
    if (!input.trim() || isTyping) return;
    setSuppressSuggestions(true);

    const userMessageId = getNewMessageId();
    const userMessage = { id: userMessageId, text: input, sender: "user" };
    const newMessages = [...messages, userMessage];
    setMessages(newMessages);
    setLastQuestion(input);
    setInput("");

    await sendMessage(input, newMessages, null);
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleClearChat = async () => {
    setMessages([]);
    setLastQuestion("");
    setPendingAction(null);
    setPendingEmail("");
    setInterruptInputs({});
    setEmailSubmitted(false);
    setEmailConfirmed(false);

    if (conversationId) {
      try {
        await axios.post(
          `${CHATBOT_API_URL}/clear-context`,
          null,
          {
            params: { conversation_id: conversationId },
            headers: { "Content-Type": "application/json" },
          }
        );
      } catch (error) {
        console.error("Failed to clear chat:", error);
      }
    }
    setConversationId(null);
  };

  const searchQuestion = (search) => {
    if (suppressSuggestions) return;
    if (!search || search.trim().length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    axios.post(`${CHATBOT_API_URL}/suggestions`,
      { message: search },
      {
        headers: {
          'Content-Type': 'application/json',
          pubid: localStorage.getItem("pub_id"),
          channelid: localStorage.getItem("currentChannel")
        }
      }
    ).then((response) => {
      const suggestionsData = response?.data?.suggestions || [];
      setSuggestions(suggestionsData);
      setShowSuggestions(suggestionsData.length > 0);
    }).catch(() => {
      setSuggestions([]);
      setShowSuggestions(false);
    });
  };

  const debouncedSearch = useRef(
    debounce((searchTerm) => {
      if (suppressSuggestions) return;
      searchQuestion(searchTerm);
    }, 500)
  ).current;

  const handleInputChange = (e) => {
    const value = e.target.value;
    setInput(value);
    if (suppressSuggestions) setSuppressSuggestions(false);
    debouncedSearch(value);
  };

  const handleSelectSuggestion = (question) => {
    setInput(question);
    setShowSuggestions(false);
  };

  const handleKeyDown = (e) => {
    if (showSuggestions && suggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedSuggestionIndex((prevIndex) => {
          if (prevIndex === suggestions.length - 1) {
            setTimeout(() => {
              setSelectedSuggestionIndex(-1);
              inputRef.current?.focus();
            }, 0);
            return -1;
          }
          return prevIndex === -1 ? 0 : Math.min(prevIndex + 1, suggestions.length - 1);
        });
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedSuggestionIndex((prevIndex) =>
          prevIndex === -1 ? suggestions.length - 1 : Math.max(prevIndex - 1, 0)
        );
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (selectedSuggestionIndex >= 0 && selectedSuggestionIndex < suggestions.length) {
          const selectedQuestion = suggestions[selectedSuggestionIndex].question;
          setInput("");
          setShowSuggestions(false);
          setTimeout(() => {
            const userMessageId = getNewMessageId();
            const userMessage = {
              id: userMessageId,
              text: selectedQuestion,
              sender: "user",
            };
            const newMessages = [...messages, userMessage];
            setMessages(newMessages);
            setLastQuestion(selectedQuestion);
            sendMessage(selectedQuestion, newMessages, null);
          }, 100);
        } else {
          handleSend();
        }
      }
    } else if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      setSuppressSuggestions(true);
      handleSend();
    }
  };

  const handleEmailSubmit = () => {
    if (!pendingEmail.trim() || emailSubmitted) return;
    if (!EMAIL_REGEX.test(pendingEmail.trim())) {
      setEmailError({ submit: "Please enter a valid email address." });
      return;
    }
    setEmailError({});
    setEmailSubmitted(true);
    
    const userMsg = {
      id: getNewMessageId(),
      text: pendingEmail,
      sender: "user",
    };
    setMessages((prev) => [...prev, userMsg]);
    sendMessage(pendingEmail, [...messages, userMsg]);
  };

  const handleEmailConfirmYes = () => {
    if (!pendingEmail.trim() || emailConfirmed) return;
    if (!EMAIL_REGEX.test(pendingEmail.trim())) {
      setEmailError({ confirm: "Please enter a valid email address." });
      return;
    }
    setEmailError({});
    setEmailConfirmed(true);
    
    const userMsg = {
      id: getNewMessageId(),
      text: `Yes, ${pendingEmail}`,
      sender: "user",
    };
    setMessages((prev) => [...prev, userMsg]);
    sendMessage(pendingEmail, [...messages, userMsg]);
  };

  const handleEmailConfirmNo = () => {
    if (emailConfirmed) return;
    setEmailConfirmed(true);
    setPendingEmail("");
    setEmailSubmitted(false);
    setPendingAction("ask_email");
  };

  const handleEmailInput = (e, interruptId) => {
    try {
      const v = e.target.value;
      setInterruptInputs((prev) => ({ ...prev, [interruptId]: v }));
      if (v && !EMAIL_REGEX.test(v.trim())) {
        setEmailError((prev) => ({ ...prev, [interruptId]: "Please enter a valid email address." }));
      } else {
        setEmailError((prev) => ({ ...prev, [interruptId]: "" }));
      }
    } catch (err) {
      console.error('interrupt input onChange error', err);
    }
  };

  return (
    <>
      {isChatBotOpen && (
        <div className="chatbot-modal position-fixed d-flex flex-column">
          <div className="chatbot-header d-flex justify-content-between align-items-center text-white position-relative">
            <h5 className="chatbot-title d-flex align-items-center text-white m-0 position-relative">
              <img
                src={ChatBotIcon}
                alt="AI Chat"
                className="mr-2"
                style={{ width: "30px", height: "30px" }}
              />
              AI Chat
            </h5>
            <div className="chatbot-header-actions d-flex align-items-center">
              <button
                className="clear-chat-icon d-flex justify-content-center align-items-center position-relative text-white rounded-circle"
                onClick={handleClearChat}
                style={{ outline: "none" }}
                title="Clear chat"
              >
                <FontAwesomeIcon icon={faTrash} />
              </button>
              <button
                className="close-icon d-flex justify-content-center align-items-center position-relative text-white rounded-circle"
                onClick={toggle}
                style={{ outline: "none" }}
                title="Close chat"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
          </div>
          <div className="chatbot-body flex-fill overflow-auto p-3">
            {messages.length === 0 ? (
              <div className="chatbot-placeholder text-center p-5 text-muted position-relative overflow-hidden">
                <img
                  src={ChatBotIcon}
                  alt="AI Chat"
                  className="mb-3"
                  style={{ width: "100px", height: "100px" }}
                />
                <p>How can I help you today?</p>
              </div>
            ) : (
              <div className="p-3">
                <>
                  {messages.map((message, index) => {
                    if (message.type === "interrupt") {
                      const waitingForEmail = Boolean(message.waiting_for_email);
                      const interruptId = message.interrupt_id || `interrupt_${message.id || index}`;
                      const isDisabled = message.buttonsDisabled || false;
                      
                      return (
                        <div
                          key={message.id}
                          className="chatbot-message-interrupt bg-light p-2 rounded mb-3"
                        >
                          <p style={{ fontWeight: 'bold' }}>{message.text}</p>
                          {waitingForEmail ? (
                            <div className="d-flex flex-column gap-2 mt-2">
                              <input
                                type="email"
                                className="form-control"
                                placeholder="Please enter your email"
                                value={interruptInputs[interruptId] || ""}
                                onChange={(e) => handleEmailInput(e, interruptId)}
                                disabled={isDisabled}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" && !e.shiftKey && !isDisabled) {
                                    e.preventDefault();
                                    const emailValue = (interruptInputs[interruptId] || "").trim();
                                    if (emailValue) {
                                      handleResume(message.interrupt_id, emailValue, emailValue);
                                    }
                                  }
                                }}
                              />
                              {emailError[interruptId] && (
                                <div className="text-danger mb-2" style={{ fontSize: "13px" }}>
                                  {emailError[interruptId]}
                                </div>
                              )}
                              <div className="d-flex gap-2 mt-2">
                                <button
                                  className="btn btn-primary btn-sm"
                                  disabled={!(interruptInputs[interruptId] || "").trim() || isDisabled}
                                  onClick={() => {
                                    const emailValue = (interruptInputs[interruptId] || "").trim();
                                    if (emailValue && EMAIL_REGEX.test(emailValue)) {
                                      handleResume(message.interrupt_id, emailValue, emailValue);
                                    }
                                  }}
                                >Submit</button>
                                <button
                                  className="secondary-new-button"
                                  disabled={isDisabled}
                                  onClick={() => {
                                    setMessages((prev) => prev.filter((msg) => msg.interrupt_id !== message.interrupt_id));
                                    handleResume(message.interrupt_id || interruptId, "no");
                                    setPendingAction(null);
                                    setPendingEmail("");
                                  }}
                                >Cancel</button>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex gap-2 mt-2">
                              <button
                                className="btn btn-success btn-sm"
                                disabled={isDisabled}
                                onClick={() => {
                                  handleResume(message.interrupt_id, "yes", message.email);
                                }}
                              >Yes</button>
                              <button
                                className="btn btn-danger btn-sm"
                                disabled={isDisabled}
                                onClick={() => {
                                  handleResume(message.interrupt_id, "no");
                                }}
                              >No</button>
                            </div>
                          )}
                        </div>
                      );
                    }
                    
                    if (message.isDynamicUI && message.sender === "bot") {
                      const isLastMessage = index === messages.length - 1;
                      return (
                        <div key={message.id}>
                          {isLastMessage && pendingAction === "ask_email" && (
                            <div className="d-flex mb-3 justify-content-start">
                              <div
                                className="chatbot-message-bot bg-light border p-3 rounded-lg"
                                style={{ maxWidth: "85%" }}
                              >
                                <p className="mb-2">
                                  Please enter your registered email address:
                                </p>
                                <input
                                  type="email"
                                  value={pendingEmail}
                                  onChange={(e) => {
                                    setPendingEmail(e.target.value);
                                    if (e.target.value && !EMAIL_REGEX.test(e.target.value.trim())) {
                                      setEmailError({ submit: "Please enter a valid email address." });
                                    } else {
                                      setEmailError({});
                                    }
                                  }}
                                  placeholder="Enter your email"
                                  className="form-control mb-2"
                                  disabled={emailSubmitted}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey && !emailSubmitted) {
                                      e.preventDefault();
                                      handleEmailSubmit();
                                    }
                                  }}
                                />
                                {emailError.submit && (
                                  <div className="text-danger mb-2" style={{ fontSize: "13px" }}>
                                    {emailError.submit}
                                  </div>
                                )}
                                <button
                                  className="btn btn-primary btn-sm"
                                  disabled={!pendingEmail.trim() || emailSubmitted || !!emailError.submit}
                                  onClick={handleEmailSubmit}
                                >Submit</button>
                              </div>
                            </div>
                          )}
                          {isLastMessage && pendingAction === "confirm_email" && (
                            <div className="d-flex mb-3 justify-content-start">
                              <div
                                className="chatbot-message-bot bg-light border p-3 rounded-lg"
                                style={{ maxWidth: "85%" }}
                              >
                                <p className="mb-2">
                                  Is this your email: <strong>{pendingEmail}</strong>?
                                </p>
                                {emailError.confirm && (
                                  <div className="text-danger mb-2" style={{ fontSize: "13px" }}>
                                    {emailError.confirm}
                                  </div>
                                )}
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-success btn-sm me-2"
                                    onClick={handleEmailConfirmYes}
                                    disabled={emailConfirmed || !!emailError.confirm}
                                  >Yes</button>
                                  <button
                                    className="btn btn-danger btn-sm"
                                    onClick={handleEmailConfirmNo}
                                    disabled={emailConfirmed}
                                  >No</button>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    }

                    return (
                      <div key={message.id}>
                        <div
                          className={`chatbot-message d-flex mb-3 ${
                            message.sender === "user"
                              ? "justify-content-end"
                              : "justify-content-start flex-column"
                          }`}
                        >
                          <div
                            className={`px-3 py-1 rounded-lg position-relative overflow-hidden ${
                              message.sender === "user"
                                ? "chatbot-message-user bg-primary text-white"
                                : "chatbot-message-bot bg-light border"
                            }`}
                            style={{ maxWidth: "85%", fontSize: "14px" }}
                          >
                            {message.sender === "bot" ? (
                              <ReactMarkdown
                                plugins={[gfm]}
                                source={message.text}
                                linkTarget="_blank"
                                renderers={{
                                  table: ({ children }) => (
                                    <div className="table-container">
                                      <table>{children}</table>
                                    </div>
                                  ),
                                }}
                              />
                            ) : (
                              message.text
                            )}
                          </div>
                          {message.sender === "bot" &&
                            !message.isStreaming &&
                            !message.isDynamicUI &&
                            message.id === messages[messages.length - 1].id && (
                              <div className="message-actions d-flex mt-2">
                                <Button
                                  color="link"
                                  size="sm"
                                  className="retry-button d-inline-block text-primary"
                                  onClick={retryLastQuestion}
                                  title="Regenerate response"
                                >
                                  <FontAwesomeIcon icon={faRedo} /> Try Again
                                </Button>
                              </div>
                            )}
                        </div>
                      </div>
                    );
                  })}
                </>
                {isTyping && (
                  <div className="d-flex mb-3 justify-content-start">
                    <div className="typing-indicator d-flex align-items-center">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                )}
                <div ref={messagesEndRef} />
              </div>
            )}
          </div>

          <div ref={inputContainerRef} className="chatbot-footer position-relative p-2">
            <InputGroup>
              <Input
                className="chat-input rounded-lg auto-resize"
                placeholder="Type your message... "
                value={input}
                onChange={handleInputChange}
                onKeyPress={handleKeyPress}
                onKeyDown={handleKeyDown}
                type="textarea"
                rows={Math.min(2, input.split("\n").length || 1)}
                style={{ resize: "none" }}
                innerRef={inputRef}
              />
              {showSuggestions && suggestions.length > 0 && (
                <div className="suggestions-list position-absolute w-100">
                  {suggestions.slice(0, 3).map((item, index) => (
                    <div
                      key={item}
                      className={`suggestion-item d-flex align-items-center p-3 ${
                        selectedSuggestionIndex === index ? "selected" : ""
                      }`}
                      onClick={() => handleSelectSuggestion(item)}
                    >
                      <div className="suggestion-icon d-flex align-items-center justify-content-center">
                        <FontAwesomeIcon icon={faSearch} />
                      </div>
                      <div className="suggestion-text text-truncate">{item}</div>
                    </div>
                  ))}
                </div>
              )}

              <InputGroupAddon addonType="append" className="ml-2">
                <div className="d-flex align-items-center">
                  <button
                    className={`mic-button position-relative overflow-hidden p-3 rounded-circle ${
                      isRecording ? "recording-active" : ""
                    }`}
                    onClick={isRecording ? stopRecording : startRecording}
                    title={isRecording ? "Stop recording" : "Start recording"}
                    style={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center"
                    }}
                  >
                    <FontAwesomeIcon icon={isRecording ? faMicrophoneSlash : faMicrophone} />
                  </button>

                  {isTyping ? (
                    <button
                      className={`stop-button position-relative overflow-hidden p-3 m-1 rounded-circle ${
                        buttonFlip ? "button-flip" : ""
                      }`}
                      onClick={stopGenerating}
                      title="Stop generating"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <FontAwesomeIcon icon={faStop} className="mb-1" />
                    </button>
                  ) : (
                    <button
                      className={`send-button position-relative overflow-hidden p-3 m-1 rounded-circle ${
                        buttonFlip ? "button-flip" : ""
                      }`}
                      onClick={handleSend}
                      disabled={!input.trim()}
                      title="Send message"
                      style={{ width: "50px", height: "50px" }}
                    >
                      <SendIcon className="mb-2 text-white" />
                    </button>
                  )}
                </div>
              </InputGroupAddon>
            </InputGroup>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;